import { type ReactNode } from "react";

import { cx } from "@/lib/utils";

export interface TabItem<T extends string = string> {
  value: T;
  label: ReactNode;
  count?: number;
}

interface TabsProps<T extends string = string> {
  items: TabItem<T>[];
  value: T;
  onChange: (next: T) => void;
  ariaLabel?: string;
  className?: string;
  testId?: string;
}

export function Tabs<T extends string = string>({
  items,
  value,
  onChange,
  ariaLabel,
  className,
  testId,
}: TabsProps<T>): JSX.Element {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cx(
        "inline-flex items-center gap-1 rounded-md border border-border bg-surface p-1",
        className,
      )}
      data-testid={testId}
    >
      {items.map((it) => {
        const active = it.value === value;
        return (
          <button
            key={it.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(it.value)}
            className={cx(
              "inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition-colors",
              active
                ? "bg-ink text-bg"
                : "text-ink-2 hover:bg-surface-2",
            )}
          >
            <span>{it.label}</span>
            {it.count !== undefined && (
              <span
                className={cx(
                  "mono rounded px-1 text-[10px] tabular-nums",
                  active ? "bg-white/20 text-white" : "bg-surface-2 text-ink-3",
                )}
              >
                {it.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
